import { useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, LogOut, ExternalLink, Menu, X } from "lucide-react";
import Dashboard from "@/admin/Dashboard";
import Leads from "@/admin/Leads";
import Singleton from "@/admin/Singleton";
import CollectionManager from "@/admin/CollectionManager";

const NAV = [
  { to: "/admin", label: "Dashboard", end: true },
  { section: "Konten" },
  { to: "/admin/home_sections", label: "Homepage" },
  { to: "/admin/pricing_tabs", label: "Harga & Paket" },
  { to: "/admin/services", label: "Services" },
  { to: "/admin/portfolio", label: "Portfolio" },
  { to: "/admin/blog_posts", label: "Blog" },
  { to: "/admin/blog_categories", label: "Kategori Blog" },
  { to: "/admin/testimonials", label: "Testimoni" },
  { to: "/admin/clients", label: "Client Logo" },
  { to: "/admin/team", label: "Tim" },
  { to: "/admin/faqs", label: "FAQ" },
  { to: "/admin/media", label: "Media Library" },
  { section: "Sistem" },
  { to: "/admin/leads", label: "Leads" },
  { to: "/admin/navigation", label: "Navigasi" },
  { to: "/admin/seo", label: "SEO" },
  { to: "/admin/settings", label: "Site Settings" },
];

export default function AdminApp() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-background"><span className="overline text-xs text-muted-foreground">Memuat…</span></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground md:flex" data-testid="admin-app">
      <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-background px-5 md:hidden">
        <div><span className="font-display text-xl font-black">Digitalis.</span><span className="ml-2 overline text-[8px] text-muted-foreground">Admin</span></div>
        <button onClick={() => setMenuOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-full" aria-label="Buka menu admin"><Menu className="h-6 w-6" /></button>
      </header>

      {menuOpen && <button className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setMenuOpen(false)} aria-label="Tutup menu admin" />}

      <aside className={`fixed inset-y-0 left-0 z-50 flex h-screen w-72 shrink-0 flex-col border-r border-border bg-background transition-transform md:sticky md:top-0 md:w-60 md:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between px-5 py-5 border-b border-border">
          <div>
          <div className="font-display font-black text-2xl">Digitalis<span className="text-muted-foreground">.</span></div>
          <p className="overline text-[9px] text-muted-foreground mt-1">Admin CMS</p>
          </div>
          <button onClick={() => setMenuOpen(false)} className="flex h-11 w-11 items-center justify-center rounded-full md:hidden" aria-label="Tutup menu admin"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n, i) => n.section ? (
            <p key={i} className="overline text-[9px] text-muted-foreground/60 px-5 mt-5 mb-2">{n.section}</p>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)}
              className={({ isActive }) => `flex min-h-11 items-center gap-2 px-5 py-2 text-sm transition-colors ${isActive ? "bg-foreground text-background font-medium" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
              data-testid={`adminnav-${n.label.toLowerCase().replace(/ /g, "-")}`}>
              {n.end && <LayoutDashboard className="w-4 h-4" />}{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3 space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex min-h-11 items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:text-foreground"><ExternalLink className="w-4 h-4" /> Lihat Website</a>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex min-h-11 items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:text-foreground w-full" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Keluar</button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 px-4 pb-8 pt-20 sm:px-6 md:p-10 max-w-full overflow-x-hidden">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="leads" element={<Leads />} />
          <Route path="seo" element={<Singleton kind="seo" />} />
          <Route path="settings" element={<Singleton kind="settings" />} />
          {["home_sections", "pricing_tabs", "services", "portfolio", "blog_posts", "blog_categories", "testimonials", "clients", "team", "faqs", "media", "navigation"].map((c) => (
            <Route key={c} path={c} element={<CollectionManager collection={c} />} />
          ))}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}
